
import React from 'react';
import { BarChart2, PieChart, LineChart } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Heading from '@/components/ui/heading';
import MessageAvatar from '@/components/MessageAvatar';
import SectionHeading from '@/components/message/report/ui/SectionHeading';
import TextParagraph from '@/components/message/report/ui/TextParagraph';
import BulletedList from '@/components/message/report/ui/BulletedList';
import ReportTable from '@/components/message/report/ui/ReportTable';

const SampleReport = () => {
  const reportSections = [
    {
      icon: <BarChart2 className="h-6 w-6 text-blue-500" />,
      title: "Competitive Landscape",
      content: "Detailed analysis of key competitors, market share distribution, and competitive advantages."
    },
    {
      icon: <LineChart className="h-6 w-6 text-blue-500" />,
      title: "Market Trends",
      content: "Identification of emerging trends, growth projections, and market drivers influencing the industry."
    },
    {
      icon: <PieChart className="h-6 w-6 text-blue-500" />,
      title: "SWOT Analysis",
      content: "Comprehensive evaluation of strengths, weaknesses, opportunities, and threats in the current market context."
    }
  ];

  const marketShareTable = `
| Company | Cloud Market Share | YoY Change |
| ------- | ------------ | ---------- |
| Microsoft Azure | 24.3% | +1.9% |
| AWS | 31.2% | -0.5% |
| Google Cloud | 11.9% | +0.7% |
| Other Providers | 32.6% | -2.1% |
  `;

  const strengths = [
    "Industry-leading AI integration across product portfolio",
    "Rapid cloud revenue growth with Azure",
    "Strong financial performance with record revenues"
  ];
  
  const weaknesses = [
    "Ongoing antitrust scrutiny and regulatory challenges",
    "Layoffs impacting talent retention and organizational culture",
    "Cybersecurity vulnerabilities in legacy products"
  ];
  
  const opportunities = [
    "Expanding cloud market with AI-driven demand",
    "Further enterprise penetration with Copilot suite",
    "Gaming ecosystem growth through Xbox and Activision Blizzard integration"
  ];
  
  const threats = [
    "Increasing competition in AI from Google, OpenAI, and Anthropic", 
    "Regulatory pressure in EU, US, and global markets",
    "Emerging cloud-native competitors in specialized sectors"
  ];

  const growthDrivers = [
    "Azure cloud business growing at 29% year-over-year in Q2 2024",
    "Microsoft Copilot and AI services generating over $10 billion annual run rate",
    "Gaming division revenue up 49% following Activision Blizzard acquisition completion"
  ];

  const marketPosition = [
    "Microsoft's market cap surpassed $3.3 trillion in May 2024, maintaining position as one of the world's most valuable companies",
    "Leadership in productivity software with Microsoft 365 holding 92% market share in enterprise",
    "AI integration becoming key differentiator across product lines with Copilot adoption exceeding initial targets"
  ];

  const recommendations = [
    "Accelerate AI product integration beyond productivity suite to maintain competitive edge against Google's Gemini and other competitors.",
    "Expand data center capacity in emerging markets to capitalize on cloud computing growth in Asia and Africa.",
    "Address regulatory concerns proactively with transparent AI governance frameworks and interoperability commitments."
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-900 to-gray-950 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/5 via-gray-900 to-gray-950 opacity-80"></div>
      <div className="absolute top-0 w-full h-px bg-gradient-to-r from-transparent via-blue-700/30 to-transparent"></div>
      
      <div className="container relative z-10 mx-auto px-4">
        <div className="text-center mb-16">
          <span className="inline-block py-1 px-3 text-sm font-medium bg-blue-900/40 text-blue-300 rounded-full backdrop-blur-sm border border-blue-800/50 mb-3">Preview</span>
          <Heading level={2} className="mb-4 text-white">
            Sample Report Preview
          </Heading>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Get a glimpse of our comprehensive market intelligence reports delivered right in your chat interface.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 max-w-6xl mx-auto">
          <div className="lg:col-span-2 space-y-6">
            {reportSections.map((section, index) => (
              <Card key={index} className="p-6 border border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50 transition-all duration-300 hover:border-blue-700/30 hover:shadow-md hover:shadow-blue-900/10 rounded-2xl">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-blue-900/20 rounded-xl">
                    {section.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-100 mb-2">
                      {section.title}
                    </h3>
                    <p className="text-gray-300 text-sm">
                      {section.content}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
            
            <div className="text-center mt-8">
              <h3 className="text-xl font-medium text-blue-300 tracking-wide">
                Premium Market Intelligence
              </h3>
            </div>
          </div>
          
          <div className="lg:col-span-3">
            <Card className="overflow-hidden border border-gray-700/50 shadow-lg hover:shadow-xl transition-all duration-500 hover:border-gray-600/50">
              <div className="p-4 bg-gray-800/50 border-b border-gray-700/30">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-100">AnalystX Chat</h3>
                  <span className="text-sm text-gray-400">Live Preview</span>
                </div>
              </div>
              
              <div className="bg-gradient-to-b from-gray-900 to-black p-5 max-h-[550px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
                <div className="py-3 bg-gradient-to-r from-gray-900/80 to-gray-900/50 rounded-lg">
                  <div className="w-full max-w-4xl mx-auto px-2">
                    <div className="flex gap-4">
                      <MessageAvatar isAssistant={true} />
                      <div className="flex-1 space-y-4">
                        <div className="prose prose-invert max-w-none">
                          <div className="flex flex-col gap-5">
                            <div className="space-y-5">
                              <h2 className="text-xl font-bold text-white">Microsoft Market Analysis Report - Q2 2024</h2>
                              
                              <div className="flex flex-wrap gap-2 mb-3">
                                <span className="px-3 py-1 bg-gradient-to-br from-gray-700/80 to-gray-700/50 backdrop-blur-sm rounded-full text-sm text-gray-200 border border-gray-600/20 shadow-sm">Technology</span>
                                <span className="px-3 py-1 bg-gradient-to-br from-gray-700/80 to-gray-700/50 backdrop-blur-sm rounded-full text-sm text-gray-200 border border-gray-600/20 shadow-sm">Cloud Computing</span>
                                <span className="px-3 py-1 bg-gradient-to-br from-gray-700/80 to-gray-700/50 backdrop-blur-sm rounded-full text-sm text-gray-200 border border-gray-600/20 shadow-sm">Artificial Intelligence</span>
                              </div>
                              
                              <SectionHeading text="Executive Summary" level="h2" />
                              <TextParagraph 
                                text="Microsoft continues to cement its position as an AI and cloud leader in 2024, with significant revenue growth across major business segments. The company's market cap has surpassed $3.3 trillion, driven by continued cloud adoption, successful integration of AI capabilities across its product portfolio, and the strategic acquisition of Activision Blizzard. While facing regulatory scrutiny and intense competition in the AI space, Microsoft's diverse revenue streams and AI-centric strategy position it well for sustained growth through 2025."
                                isExecutiveSummary={true}
                              />
                              
                              <SectionHeading text="Introduction & Methodology" level="h2" />
                              <TextParagraph 
                                text="This analysis evaluates Microsoft's current market position based on Q1-Q2 2024 financial results, executive statements, regulatory filings, and industry analyst reports. The methodology combines quantitative assessment of financial performance with qualitative analysis of strategic initiatives, competitive positioning, and market trends affecting Microsoft's business units through mid-2024."
                              />
                              
                              <SectionHeading text="Market Overview & Industry Trends" level="h2" />
                              <TextParagraph 
                                text="The global cloud computing market reached approximately $626 billion in 2023 and is projected to grow at a CAGR of 20.5% through 2030. The AI software market is experiencing even faster growth at 37.3% CAGR, expected to reach $251 billion by 2027. Microsoft has strategically positioned itself at the intersection of both high-growth markets while maintaining strength in traditional software and expanding its gaming presence."
                              />
                              
                              <SectionHeading text="Key Industry Trends" level="h3" />
                              <BulletedList items={[
                                "Generative AI integration becoming standard in enterprise software with focus on productivity enhancement",
                                "Edge computing and hybrid cloud architectures emerging as preferred deployment models",
                                "Increased regulatory scrutiny on AI systems and large technology platforms globally",
                                "Talent competition intensifying for AI expertise despite ongoing tech industry layoffs"
                              ]} />
                              
                              <SectionHeading text="Market Position" level="h3" />
                              <BulletedList items={marketPosition} />
                              
                              <SectionHeading text="Competitive Analysis" level="h2" />
                              <ReportTable content={marketShareTable} />
                              
                              <SectionHeading text="Key Competitive Advantages" level="h3" />
                              <BulletedList items={[
                                "First-mover advantage in enterprise AI with comprehensive Copilot integration across product suite",
                                "Strong positioning in both cloud infrastructure and AI application layers",
                                "Strategic OpenAI partnership providing exclusive early access to frontier AI capabilities",
                                "Extensive enterprise relationships and mature partner ecosystem"
                              ]} />
                              
                              <SectionHeading text="Competitive Dynamics" level="h3" />
                              <TextParagraph 
                                text="Microsoft's competitive landscape has intensified in 2024. In cloud, while AWS maintains market share leadership, Microsoft Azure is growing faster and narrowing the gap. Google Cloud's AI-first approach is driving its improved competitive position. In the AI space, Microsoft faces challenges from OpenAI's direct consumer offerings, Google's Gemini models, and Anthropic's Claude. Competitive pressure has increased as major cloud providers rapidly integrate foundation models into their offerings while enterprise software vendors race to incorporate AI assistants."
                              />
                              
                              <SectionHeading text="Key Growth Drivers" level="h3" />
                              <BulletedList items={growthDrivers} />
                              
                              <SectionHeading text="Digital Transformation Initiatives" level="h3" />
                              <BulletedList items={[
                                "Microsoft 365 Copilot adoption exceeding expectations with 60% of Fortune 500 companies now using the service",
                                "Azure OpenAI Service expanded with new models and capabilities including multimodal functionality",
                                "Security Copilot gaining traction as a key differentiator in the cybersecurity landscape"
                              ]} />
                              
                              <SectionHeading text="SWOT Analysis" level="h2" />
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                <div className="p-3 bg-blue-900/20 rounded-lg border border-blue-800/30">
                                  <h4 className="font-medium text-blue-300 mb-2">Strengths</h4>
                                  <ul className="list-disc pl-5 text-sm text-gray-300 space-y-1">
                                    {strengths.map((item, i) => (
                                      <li key={i}>{item}</li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="p-3 bg-red-900/20 rounded-lg border border-red-800/30">
                                  <h4 className="font-medium text-red-300 mb-2">Weaknesses</h4>
                                  <ul className="list-disc pl-5 text-sm text-gray-300 space-y-1">
                                    {weaknesses.map((item, i) => (
                                      <li key={i}>{item}</li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="p-3 bg-green-900/20 rounded-lg border border-green-800/30">
                                  <h4 className="font-medium text-green-300 mb-2">Opportunities</h4>
                                  <ul className="list-disc pl-5 text-sm text-gray-300 space-y-1">
                                    {opportunities.map((item, i) => (
                                      <li key={i}>{item}</li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="p-3 bg-yellow-900/20 rounded-lg border border-yellow-800/30">
                                  <h4 className="font-medium text-yellow-200 mb-2">Threats</h4>
                                  <ul className="list-disc pl-5 text-sm text-gray-300 space-y-1">
                                    {threats.map((item, i) => (
                                      <li key={i}>{item}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                              
                              <SectionHeading text="Financial Performance" level="h2" />
                              <TextParagraph 
                                text="Microsoft reported annual revenue of $235.8 billion for fiscal year 2023 and is on track to exceed $250 billion in FY2024 based on current growth rates. In Q2 2024, the company reported revenue of $62.02 billion, representing a 17% year-over-year increase. The Intelligent Cloud segment continues to be the primary growth driver with $24.3 billion in revenue, up 29% year-over-year. Notably, AI services contributed significantly to growth with Microsoft Copilot and related AI services reaching a $10 billion annual run rate."
                              />
                              
                              <SectionHeading text="Future Outlook & Strategic Recommendations" level="h2" />
                              <TextParagraph 
                                text="Microsoft is well-positioned for continued growth through 2025, with AI capabilities serving as a key differentiator across its product portfolio. The company's balance of cloud infrastructure, productivity software, AI services, and gaming positions it uniquely in the tech ecosystem. Growth is expected to continue at double-digit rates, with AI adoption driving premium service revenue and cloud usage expansion."
                              />
                              
                              <SectionHeading text="Strategic Recommendations" level="h3" />
                              <ol className="list-decimal pl-5 text-sm text-gray-300 space-y-2 my-3">
                                {recommendations.map((rec, i) => (
                                  <li key={i} className="mb-2">
                                    <strong className="text-white">{rec.split(' to ')[0]}</strong> to {rec.split(' to ')[1]}
                                  </li>
                                ))}
                              </ol>
                              
                              <SectionHeading text="References" level="h2" />
                              <ul className="space-y-3 list-decimal pl-5">
                                <li className="text-blue-400 hover:text-blue-300 hover:underline">
                                  <a href="https://www.microsoft.com/investor/reports/ar24/index.html" target="_blank" rel="noopener noreferrer">
                                    Microsoft Corporation (2024). Q2 FY24 Earnings Report.
                                  </a>
                                </li>
                                <li className="text-blue-400 hover:text-blue-300 hover:underline">
                                  <a href="https://www.gartner.com/en/newsroom/press-releases/2024-04-19-gartner-forecasts-worldwide-public-cloud-end-user-spending-to-grow-22-percent-in-2024" target="_blank" rel="noopener noreferrer">
                                    Gartner (2024). Worldwide Public Cloud End-User Spending Forecast.
                                  </a>
                                </li>
                                <li className="text-blue-400 hover:text-blue-300 hover:underline">
                                  <a href="https://www.idc.com/getdoc.jsp?containerId=prUS51264724" target="_blank" rel="noopener noreferrer">
                                    IDC (2024). Worldwide Public Cloud Services Tracker.
                                  </a>
                                </li>
                                <li className="text-blue-400 hover:text-blue-300 hover:underline">
                                  <a href="https://www.forrester.com/report/the-forrester-wave-aiinfused-cloud-platforms-q1-2024/RES179830" target="_blank" rel="noopener noreferrer">
                                    Forrester Research (2024). The Forrester Wave™: AI-Infused Cloud Platforms.
                                  </a>
                                </li>
                                <li className="text-blue-400 hover:text-blue-300 hover:underline">
                                  <a href="https://www.mckinsey.com/capabilities/quantumblack/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier" target="_blank" rel="noopener noreferrer">
                                    McKinsey & Company (2024). The Economic Potential of Generative AI: Updated Analysis.
                                  </a>
                                </li>
                              </ul>
                            </div>
                            
                            <div className="mt-4 flex gap-2">
                              <Button size="sm" variant="outline" className="text-xs">
                                <svg className="h-3 w-3 mr-1" viewBox="0 0 384 512">
                                  <path fill="currentColor" d="M369.9 97.9L286 14C277 5 264.8-.1 252.1-.1H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V131.9c0-12.7-5.1-25-14.1-34zM332.1 128H256V51.9l76.1 76.1zM48 464V48h160v104c0 13.3 10.7 24 24 24h104v288H48z"></path>
                                </svg>
                                PDF
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SampleReport;
